/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useEffect, useState } from 'react'
import * as z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getUserModels } from '@/lib/api'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { getPricing } from '@/features/pricing/api'
import { AnnouncementEmailDialog } from '../components/announcement-email-dialog'
import { SettingsSection } from '../components/settings-section'
import { useUpdateOption } from '../hooks/use-update-option'
import { filterTextModelOptions, isTextModel } from '../utils/text-models'

const noticeSchema = z.object({
  Notice: z.string().optional(),
  NoticeTranslateModel: z.string().optional(),
  NoticeTranslateToken: z.string().optional(),
})

type NoticeFormValues = z.infer<typeof noticeSchema>

type NoticeSectionProps = {
  defaultValue: string
  defaultTranslateModel: string
  defaultTranslateToken: string
}

export function NoticeSection({
  defaultValue,
  defaultTranslateModel,
  defaultTranslateToken,
}: NoticeSectionProps) {
  const { t } = useTranslation()
  const updateOption = useUpdateOption()
  const [broadcastOpen, setBroadcastOpen] = useState(false)
  const form = useForm<NoticeFormValues>({
    resolver: zodResolver(noticeSchema),
    defaultValues: {
      Notice: defaultValue ?? '',
      NoticeTranslateModel: defaultTranslateModel ?? '',
      NoticeTranslateToken: defaultTranslateToken ?? '',
    },
  })

  useEffect(() => {
    form.reset({
      Notice: defaultValue ?? '',
      NoticeTranslateModel: defaultTranslateModel ?? '',
      NoticeTranslateToken: defaultTranslateToken ?? '',
    })
  }, [defaultValue, defaultTranslateModel, defaultTranslateToken, form])

  const [modelOptions, setModelOptions] = useState<string[]>([])
  useEffect(() => {
    Promise.all([getUserModels(), getPricing().catch(() => null)])
      .then(([response, pricing]) =>
        setModelOptions(
          filterTextModelOptions(response.data ?? [], pricing?.data)
        )
      )
      .catch(() => setModelOptions([]))
  }, [])

  const onSubmit = async (values: NoticeFormValues) => {
    const updates: Array<{ key: string; value: string }> = []
    if ((values.Notice ?? '') !== (defaultValue ?? '')) {
      updates.push({ key: 'Notice', value: values.Notice ?? '' })
    }
    if ((values.NoticeTranslateModel ?? '') !== (defaultTranslateModel ?? '')) {
      updates.push({
        key: 'NoticeTranslateModel',
        value: values.NoticeTranslateModel ?? '',
      })
    }
    // Sensitive tokens are intentionally omitted by GET /api/option/. An
    // empty field means “keep the existing token”, never clear it.
    if ((values.NoticeTranslateToken ?? '').trim() !== '') {
      updates.push({
        key: 'NoticeTranslateToken',
        value: values.NoticeTranslateToken!.trim(),
      })
    }
    for (const update of updates) {
      await updateOption.mutateAsync(update)
    }
  }

  return (
    <SettingsSection
      title={t('System Notice')}
      description={t(
        'Broadcast a global banner to users. Markdown is supported.'
      )}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
          <FormField
            control={form.control}
            name='Notice'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('Announcement content')}</FormLabel>
                <FormControl>
                  <Textarea
                    rows={8}
                    placeholder={t(
                      'Planned maintenance on Friday at 22:00 UTC...'
                    )}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t(
                    'This is the global system notice shown in the top banner and system-notice dialog. It is separate from dashboard console announcements.'
                  )}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className='grid gap-6 md:grid-cols-2'>
            <FormField
              control={form.control}
              name='NoticeTranslateModel'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Announcement translation model')}</FormLabel>
                  <Select
                    value={field.value || undefined}
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger className='w-full font-mono'>
                        <SelectValue placeholder={t('Select a text model')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent alignItemWithTrigger={false}>
                      <SelectGroup>
                        {field.value &&
                          isTextModel(field.value) &&
                          !modelOptions.includes(field.value) && (
                            <SelectItem value={field.value}>
                              {field.value}
                            </SelectItem>
                          )}
                        {modelOptions.map((model) => (
                          <SelectItem key={model} value={model}>
                            {model}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {t(
                      'Used for both system notices and console announcements. Only text models are listed.'
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='NoticeTranslateToken'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Announcement translation token')}</FormLabel>
                  <FormControl>
                    <Input
                      type='password'
                      autoComplete='new-password'
                      placeholder={t('Leave blank to keep the current token')}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t(
                      'An internal API token used only when translating announcements.'
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className='flex flex-wrap gap-2'>
            <Button type='submit' disabled={updateOption.isPending}>
              {updateOption.isPending ? t('Saving...') : t('Save notice')}
            </Button>
            <Button
              type='button'
              variant='outline'
              onClick={() => setBroadcastOpen(true)}
              disabled={
                updateOption.isPending ||
                form.formState.isDirty ||
                !defaultValue.trim()
              }
              title={
                form.formState.isDirty
                  ? t('Save the notice before sending email')
                  : undefined
              }
            >
              <Mail data-icon='inline-start' />
              {t('Email broadcast')}
            </Button>
          </div>
        </form>
      </Form>
      <AnnouncementEmailDialog
        open={broadcastOpen}
        onOpenChange={setBroadcastOpen}
        source='system_notice'
      />
    </SettingsSection>
  )
}
